import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, coverUrl, trackView, setPageMeta } from "@/lib/api";
import { formatPostDate } from "@/lib/date";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { ArrowLeft } from "lucide-react";

const PostDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/posts/${id}`);
        setPost(data);
        // SEO: dynamic meta + analytics
        const desc = (data.excerpt || "").slice(0, 200);
        setPageMeta({
          title: data.title,
          description: desc || undefined,
          image: coverUrl(data) || undefined,
          url: window.location.href,
        });
        trackView(`/post/${id}`, id);
      } catch (e) {
        setError(e?.response?.data?.detail || "Post non trovato");
      }
    })();
  }, [id]);

  return (
    <div data-testid="post-detail-page" className="min-h-screen bg-[#f9f8f6]">
      <PublicHeader />

      {error ? (
        <div data-testid="post-error" className="max-w-3xl mx-auto px-6 py-32 text-center">
          <p className="font-serif text-3xl text-[#1a1918] mb-3">{error}</p>
          <Link to="/" className="font-sans text-xs tracking-[0.3em] uppercase border-b border-[#1a1918]">
            torna al diario
          </Link>
        </div>
      ) : !post ? (
        <div data-testid="post-loading" className="max-w-3xl mx-auto px-6 py-32 font-sans text-sm text-[#4a4843]">
          caricamento…
        </div>
      ) : (
        <article className="pb-24">
          <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-16">
            <Link
              to="/"
              data-testid="back-link"
              className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.3em] uppercase text-[#4a4843] hover:text-[#1a1918] mb-12"
            >
              <ArrowLeft size={14} /> indietro
            </Link>

            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#4a4843] mb-6">
              {formatPostDate(post.created_at)}
            </p>
            <h1
              data-testid="post-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light text-[#1a1918]"
            >
              {post.title}
            </h1>
            {post.author && (
              <p
                data-testid="post-author"
                className="font-serif italic text-lg sm:text-xl text-[#4a4843] mt-4"
              >
                di {post.author}
              </p>
            )}
            <div className="w-12 h-px bg-[#d4b886] my-12" />
          </div>

          {post.cover_image_url && (
            <div className="max-w-5xl mx-auto px-6 sm:px-10 mb-16">
              <img
                data-testid="post-cover"
                src={coverUrl(post)}
                alt={post.title}
                className="w-full max-h-[70vh] object-cover"
              />
            </div>
          )}
          {!post.cover_image_url && post.youtube_id && (
            <div className="max-w-4xl mx-auto px-6 sm:px-10 mb-16">
              <div className="aspect-video w-full">
                <iframe
                  data-testid="post-youtube"
                  src={`https://www.youtube.com/embed/${post.youtube_id}`}
                  title={post.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          )}

          <div className="max-w-3xl mx-auto px-6 sm:px-10">
            <div
              data-testid="post-content"
              className="prose-attraversando"
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              dangerouslySetInnerHTML={{ __html: post.content_html || "<p><em>Nessun contenuto.</em></p>" }}
            />
          </div>
        </article>
      )}

      <PublicFooter />
    </div>
  );
};

export default PostDetail;
